import React, { useEffect, useRef, useState } from 'react';
import Tool, {
  type IToolAvailableInput,
  type ToolButtonComponent,
} from '../Tool';
import ToolbarMenu from '../../toolbars/ToolbarMenu';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../../toolbars/ToolButton';
import WhiteboardController from '../../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import ToolWireframe from './ToolWireframe';
import styles from '../../styles.module.css';
import ToolButtonWithLabel from '../../toolbars/ToolButtonWithLabel';
import { isWireframe } from '../../objects/guards';
import type Wireframe from '../../objects/Wireframe';
import { camelToSentenceCase } from '../../utils/string';
import { WIREFRAME_SELECTION_ERROR_MESSAGE } from '../../utils/tools';
import { svgToIcon } from '../../utils/svg';
import { isEqual } from 'lodash';

interface PopupProps {
  currentComponentState: string;
  setCurrentComponentState: (state: string) => void;
  currentUIState: string;
  setCurrentUIState: (state: string) => void;
  selection: Wireframe | fabric.Object;
  hidePopup: () => void;
}

const Popup: React.FC<PopupProps> = ApplicationView(
  ({
    currentComponentState,
    setCurrentComponentState,
    currentUIState,
    setCurrentUIState,
    selection,
    hidePopup,
  }) => {
    if (!isWireframe(selection)) {
      console.error(WIREFRAME_SELECTION_ERROR_MESSAGE);
      return null;
    }

    const controller = useController(WhiteboardController);
    const uiStates = selection.wireframe.uiStates;
    const componentStates = selection.wireframe.componentStates();
    const showTooltips = selection.wireframe.showComponentStatesTooltips;
    const componentStateKeys = Object.keys(componentStates);

    const setUIState = uiState => {
      setCurrentUIState(uiState);
      selection.set('uiState', uiState);
      hidePopup();
      Tool.toolActionComplete({ controller });
    };

    const setComponentState = componentState => {
      setCurrentComponentState(componentState);
      selection.set('componentState', componentState);
      hidePopup();
      Tool.toolActionComplete({ controller });
    };

    return (
      <div>
        {componentStateKeys.length > 0 && (
          <>
            <div className={styles.popupSectionHeading}>
              {selection.wireframe.componentStatesTitle}
            </div>
            <ToolbarMenu columns={componentStateKeys.length ?? 2}>
              {componentStateKeys.map(state => (
                <ToolButton
                  active={state === currentComponentState}
                  key={state}
                  onClick={() => setComponentState(state)}
                  name={showTooltips ? camelToSentenceCase(state) : undefined}
                >
                  {svgToIcon(componentStates[state])}
                </ToolButton>
              ))}
            </ToolbarMenu>
          </>
        )}
        {componentStateKeys?.length > 0 && (
          <div className={styles.popupSectionHeading}>Disabled state</div>
        )}
        <div>
          {uiStates.map(state => (
            <ToolButton
              active={state === currentUIState}
              className={styles.stringToolButton}
              key={state}
              onClick={() => setUIState(state)}
            >
              {camelToSentenceCase(state)}
            </ToolButton>
          ))}
        </div>
      </div>
    );
  }
);

const UIStateToolButton: ToolButtonComponent = ApplicationView(
  ({ selection }) => {
    const tippyRef = useRef<TippyInstance>();
    if (!isWireframe(selection)) {
      console.error(WIREFRAME_SELECTION_ERROR_MESSAGE);
      return null;
    }

    const initialUIState = selection.uiState ?? 'default';
    const [state, setState] = useState(initialUIState);
    const initialComponentState = selection.componentState;
    const [componentState, setComponentState] = useState(initialComponentState);
    const [componentStates, setComponentStates] = useState(
      selection.wireframe.componentStates()
    );

    useEffect(() => {
      const handleSelectionModified = () => {
        const newComponentStates = selection.wireframe.componentStates();
        if (!isEqual(newComponentStates, componentStates)) {
          setComponentStates(newComponentStates);
        }
      };

      selection.on('modified', handleSelectionModified);
      return () => {
        selection.off('modified', handleSelectionModified);
      };
    }, [componentStates, selection, state]);

    return (
      <ToolButtonWithLabel
        popup={
          <Popup
            currentComponentState={componentState}
            setCurrentComponentState={setComponentState}
            currentUIState={state}
            setCurrentUIState={setState}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        label={'State'}
        tippyRef={tippyRef}
      >
        <div className={styles.toolButtonWideContent}>
          {componentState?.length > 0 && (
            <span>{svgToIcon(componentStates[componentState])}</span>
          )}
          {camelToSentenceCase(state)}
        </div>
      </ToolButtonWithLabel>
    );
  }
);

class ToolWireframeComponentState extends ToolWireframe {
  static toolName = 'State';

  static available({ selection }: IToolAvailableInput) {
    return selection?.type === 'wireframe';
  }

  static buttonComponent() {
    return UIStateToolButton;
  }
}

export default ToolWireframeComponentState;
